import {Component, computed, defineComponent, reactive, Reactive, watch} from "vue";
import {
    EActivityStatus,
    IActivityBusinessMeta,
    IActivityCardMetadata,
    IActivityMetadata,
    IActivityNotifyRule,
    IActivityTheme
} from "@/model/activity";
import {UserAgent} from "@/model/user_agent";
import {Material} from "@/model/material";
import {Theme} from "@/views/editor/service/theme";
import {ItemType, RadioChangeEvent} from "ant-design-vue";
import {MenuInfo} from "ant-design-vue/es/menu/src/interface";
import {
    emptyBiz,
    IBiz,
    IBizAtmosphere,
    IBizChallenge,
    IBizCoupon,
    IBizProfitSharing,
    IBizPromotion,
    IBizRaffle,
    IBizRedEnvelope,
    IBizSign,
    IBusiness,
    INewBusiness,
} from "@/views/editor/service/business";
import atmosphere from "@/views/editor/service/atmosphere";
import promotion from "@/views/editor/service/promotion";
import challenge from "@/views/editor/service/challenge";
import raffle from "@/views/editor/service/raffle";
import coupon from "@/views/editor/service/coupon";
import profit_sharing from "@/views/editor/service/profit_sharing";
import sign from "@/views/editor/service/sign";
import red_envelope from "@/views/editor/service/red_envelope";
import {IRender, ISection, ISectionController} from "@/views/editor/service/types";
import {api} from "@/lib/api";
import dayjs from "dayjs";
import emit from "@/lib/emit";
import {objCopy} from "@/lib/util";

const businessesClz: { [key: string]: { [key: string]: INewBusiness } } = {
    'atmosphere': atmosphere,
    'promotion': promotion,
    'challenge': challenge,
    'raffle': raffle,
    'coupon': coupon,
    'profit_sharing': profit_sharing,
    'red_envelope': red_envelope,
    'sign': sign,
};

export interface INewEditor {
    new(userAgent: UserAgent, material: Material, meta: IActivityMetadata): AEditor;
}

export abstract class AEditor {

    readonly meta: Reactive<IActivityMetadata>;
    readonly active = reactive({
        menu: {
            items: [] as ItemType[],
            toggle: false,
            openKeys: ['active.page'] as string[],
            selectedKeys: ['active.page'] as string[],
            keyPath: [] as string[],
        },
        content: {layer: defineComponent({}) as Component, CTUIs: [] as Component[], DPUIs: [] as Component[]},
        preview: {
            branch_id: null as null | string,
            share_title: {active: '', preheating: ''} as Record<EActivityStatus, string>,
            status: EActivityStatus.active
        },
        status: {loading: false, lastSaveTS: ''},
    })

    // UI
    readonly theme: Theme;
    readonly section: { [key: string]: ISection } = {};

    // 模块
    readonly businesses: Record<EActivityStatus, IBusiness[]> = {preheating: [], active: [], finished: []};
    abstract readonly customBusinessRange: Record<EActivityStatus, string[]>
    readonly biz: Record<EActivityStatus, IBiz> = {preheating: emptyBiz(), active: emptyBiz(), finished: emptyBiz()};

    // 商户
    readonly merchantRateLimit = computed(() => {
        let x = 10000
        for (const bid in this.meta.branches) {
            [...this.userAgent.platformSubjects, ...this.userAgent.subjects()].forEach(m => {
                if (m.merchant.merchant_id == this.meta.branches[bid].merchant_id) {
                    if (x > m.merchant.rate) x = m.merchant.rate
                }
            })
        }
        x = x == 10000 ? 0 : x
        return x / 100
    })


    protected constructor(public readonly userAgent: UserAgent, public readonly material: Material, meta: IActivityMetadata) {
        // 初始化数据
        this.meta = reactive(meta);
        this.initSectionUI()
        this.theme = new Theme(this);
        meta.activity_id || this.initBiz()
        Object.values(EActivityStatus).forEach(k => this.businesses[k] = this.meta.businesses[k].map(abm => meta.activity_id ? this.genBiz(k, abm) : this.genBiz(k, abm).resetMeta(k)))

        // 初始化UI
        this.bizMetaToUX(EActivityStatus.preheating)
        this.bizMetaToUX(EActivityStatus.active)
        this.initMenu();
        this.onMenuClick({
            domEvent: new MouseEvent(''),
            eventKey: "",
            eventKeyPath: [],
            item: {},
            key: '',
            keyPath: ['active.page']
        })

        this.syncNotifyRules()

        watch(() => [this.active.preview.branch_id, this.userAgent.meta.brands], () => emit.emit('editor.refresh'), {deep: true})
    }

    protected abstract initSectionUI(): void

    /**
     * 根据自己的版本部署自己的菜单
     * @protected
     */
    protected abstract initMenu(): void

    private bizMetaToUX(status: EActivityStatus) {
        const targets = this.bizPaths(status)
        this.meta.ux.pages[status].action_bar = Object.keys(this.section).filter(k => k.startsWith(`${status}.action.bar`)).map(k => this.section[k].meta)
        this.meta.ux.pages[status].body = Object.keys(this.section).filter(k => k.startsWith(`${status}.body`)).map(k => this.section[k].meta).filter(s => this.auth(s.dependencies, targets))
        this.meta.ux.pages[status].status_bar = Object.keys(this.section).filter(k => k.startsWith(`${status}.status.bar`)).map(k => this.section[k].meta)
        this.meta.ux.pages[status].popups = Object.keys(this.section).filter(k => k.startsWith(`${status}.popups`)).map(k => this.section[k].meta).filter(s => this.auth(s.dependencies, targets))
    }

    /**
     * 新创建活动时初始化业务数据，并根据数据部署其它对象的相关业务
     * @protected
     */
    protected abstract initBiz(): void

    protected abstract syncToMenu(): void

    /**
     * 点击菜单时
     * @param info
     */
    abstract onMenuClick(info: MenuInfo): void;

    syncNotifyRules() {
        const targets = [...this.bizPaths(EActivityStatus.preheating), ...this.bizPaths(EActivityStatus.active)]
        //先清理
        const ks: Record<string, IActivityNotifyRule> = {}
        const l = this.meta.ux.notify_rules.length - 1
        for (let i = l; i >= 0; i--) {
            ks[this.meta.ux.notify_rules[i].name] = this.meta.ux.notify_rules[i]
            if (!this.auth(this.meta.ux.notify_rules[i].dependencies, targets)) this.meta.ux.notify_rules.splice(i, 1)
        }
        // 后补充
        this.userAgent.meta.notifyRuleTemplates.forEach(r => {
            if (ks[r.name]) return
            else this.meta.ux.notify_rules.push(objCopy(r))
        })
    }

    onBizChange(status: EActivityStatus, kind: string, evt: boolean): void {
        const targets = this.bizPaths(status)
        if (evt) {
            this.meta.ux.pages[status].body.push(...Object.keys(this.section).filter(k => k.startsWith(`${status}.body`))
                .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(kind)))
                .filter(s => this.auth(s.dependencies, targets)))
            this.meta.ux.pages[status].popups.push(...Object.keys(this.section).filter(k => k.startsWith(`${status}.popups`))
                .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(kind)))
                .filter(s => this.auth(s.dependencies, targets)))
            if (status == EActivityStatus.preheating && kind == 'promotion') {
                if (this.biz.preheating.coupon) Object.assign(this.biz.preheating.coupon.meta,
                    {lock: false, enabled: false})
                if (this.biz.preheating.raffle) Object.assign(this.biz.preheating.raffle.meta,
                    {lock: false, enabled: false})
                if (this.biz.preheating.challenge) Object.assign(this.biz.preheating.challenge.meta,
                    {lock: false, enabled: true})
                if (this.biz.preheating.profit_sharing) Object.assign(this.biz.preheating.profit_sharing.meta,
                    {lock: false, enabled: false})
            }
        } else {
            this.meta.ux.pages[status].body = this.meta.ux.pages[status].body.filter(b => !b.dependencies.some(d => d.startsWith(kind)))
                .filter(s => this.auth(s.dependencies, targets))
            this.meta.ux.pages[status].popups = this.meta.ux.pages[status].popups.filter(b => !b.dependencies.some(d => d.startsWith(kind)))
                .filter(s => this.auth(s.dependencies, targets))
            if (status == EActivityStatus.preheating && kind == 'promotion') {
                if (this.biz.preheating.coupon) Object.assign(this.biz.preheating.coupon.meta,
                    {lock: true, enabled: false})
                if (this.biz.preheating.raffle) Object.assign(this.biz.preheating.raffle.meta,
                    {lock: true, enabled: false})
                if (this.biz.preheating.challenge) Object.assign(this.biz.preheating.challenge.meta,
                    {lock: true, enabled: false})
                if (this.biz.preheating.profit_sharing) Object.assign(this.biz.preheating.profit_sharing.meta,
                    {lock: true, enabled: false})
            }
        }
        this.syncToMenu()
        this.syncNotifyRules()
        console.log('onBizChange:', status, kind, evt, this.meta.ux.pages[status].body.length);
    }

    onImplementerChange(status: EActivityStatus, evt: RadioChangeEvent, meta: IActivityBusinessMeta): void {
        console.log('onImplementerChange:', evt.target.value, meta);
        const b = this.genBiz(status, meta);
        b.resetMeta(status)
        this.businesses[status][this.businesses[status].findIndex(b => b.meta.kind == meta.kind)] = b

        this.meta.ux.pages[status].body = this.meta.ux.pages[status].body.filter(b => !b.dependencies.some(d => d.startsWith(meta.kind)))
        this.meta.ux.pages[status].popups = this.meta.ux.pages[status].popups.filter(b => !b.dependencies.some(d => d.startsWith(meta.kind)))

        const targets = this.bizPaths(status)
        this.meta.ux.pages[status].body.push(...Object.keys(this.section).filter(k => k.startsWith(`${status}.body`))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(meta.kind)))
            .filter(s => this.auth(s.dependencies, targets)))
        this.meta.ux.pages[status].popups.push(...Object.keys(this.section).filter(k => k.startsWith(`${status}.popups`))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(meta.kind)))
            .filter(s => this.auth(s.dependencies, targets)))
        this.syncToMenu()
        this.syncNotifyRules()
        console.log('onImplementerChange:', this.meta.ux);
    }

    onBizSubChange(status: EActivityStatus, subs: string[], meta: IActivityBusinessMeta): void {
        const prefix = meta.kind + '/' + meta.implementer + '/'
        this.meta.ux.pages[status].body = this.meta.ux.pages[status].body.filter(b => !b.dependencies.some(d => d.startsWith(prefix)))
        this.meta.ux.pages[status].popups = this.meta.ux.pages[status].popups.filter(b => !b.dependencies.some(d => d.startsWith(prefix)))

        const targets = this.bizPaths(status)
        this.meta.ux.pages[status].body.push(...Object.keys(this.section).filter(k => k.startsWith(`${status}.body`))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(prefix)))
            .filter(s => this.auth(s.dependencies, targets)))
        this.meta.ux.pages[status].popups.push(...Object.keys(this.section).filter(k => k.startsWith(`${status}.popups`))
            .map(k => this.section[k].meta).filter(s => s.dependencies.some(d => d.startsWith(prefix)))
            .filter(s => this.auth(s.dependencies, targets)))
        console.log('onBizSubChange:', objCopy(this.meta.ux));
        this.syncToMenu()
        this.syncNotifyRules()
    }

    protected genBiz(status: EActivityStatus, meta: IActivityBusinessMeta): IBusiness {
        const b = new businessesClz[meta.kind][meta.implementer](this, meta);
        switch (meta.kind) {
            case 'atmosphere':
                this.biz[status].atmosphere = b as IBizAtmosphere;
                return b
            case 'promotion':
                this.biz[status].promotion = b as IBizPromotion;
                return b
            case 'challenge':
                this.biz[status].challenge = b as IBizChallenge;
                return b
            case 'raffle':
                this.biz[status].raffle = b as IBizRaffle;
                return b
            case 'coupon':
                this.biz[status].coupon = b as IBizCoupon;
                return b
            case 'profit_sharing':
                this.biz[status].profit_sharing = b as IBizProfitSharing;
                return b
            case 'red_envelope':
                this.biz[status].red_envelope = b as IBizRedEnvelope
                return b
            case 'sign':
                this.biz[status].sign = b as IBizSign;
                return b
            default:
                throw new Error(`Unknown meta: ${meta.kind}`)
        }
    }

    bizPaths(status: EActivityStatus, kind?: string): string[] {
        const arr: string[] = kind ? [] : ['activity', 'activity/theme', 'activity/ad']
        for (const b of this.meta.businesses[status]) {
            if (b.enabled && (kind ? kind == b.kind : true)) {
                arr.push(b.kind + '/' + b.implementer)
                b.subs.forEach(sb => {
                    arr.push(b.kind + '/' + b.implementer + '/' + sb)
                })
            }
        }
        return arr
    }

    protected addSection(id: string, name: string, dependencies: string[], displayer: Component, controllers: () => ISectionController[], can_hide?: boolean, card?: IActivityCardMetadata) {
        this.section[id] = {
            meta: {id, name, dependencies, can_hide: can_hide === undefined ? true : can_hide, is_hide: false, card},
            displayer, controllers
        }
    }

    protected toBizMeta(kind: string, implementer: string, lock?: boolean, enabled?: boolean): IActivityBusinessMeta {
        return {
            kind, lock: lock ? lock : false, enabled: enabled == undefined ? true : enabled, implementer, setting: {},
            subs: []
        }
    }

    protected _gsc(title: string, component: Component, freeze?: boolean, open?: boolean, lock?: boolean): ISectionController {
        return {title, freeze: freeze ?? true, open: open ?? true, lock: lock ?? false, component}
    }


    public auth(dependencies: string[], targets?: string[]): boolean {
        const ts = targets ?? this.bizPaths(this.active.preview.status)
        return dependencies.every(dependency => ts.some(target => target.startsWith(dependency)))
    }

    async titleVariables(): Promise<string[]> {
        return api<void, string[]>({
            url: `user/${this.userAgent.meta.account.agent_id}/activity/transform/variables`,
            method: 'post',
            data: {activity: this.meta, status: EActivityStatus.active}
        });
    }

    async titlePreview(title: string): Promise<string> {
        const data: IRender = {
            activity: this.meta,
            status: EActivityStatus.active,
        }
        const _br = this.userAgent.branch(this.active.preview.branch_id ?? '')
        if (_br) {
            data.branch = _br.meta
            data.brand = this.userAgent.brands[_br.meta.brand_id].meta
        }

        return api<void, any>({
            url: `user/${this.userAgent.meta.account.agent_id}/activity/transform`,
            method: 'POST',
            data: Object.assign(data, {text: title}),
        }).then(res => res.title)
    }

    currentBiz():IBiz {
        return this.biz[this.active.preview.status]
    }

    public async save(): Promise<void> {
        //
    }

    public async publish(): Promise<void> {
        //
    }

}

export const GenActivityMeta = async (ua: UserAgent, material: Material, tags: string[], theme_id: string | null): Promise<IActivityMetadata> => {
    const tt = material.titles.filter(tags)?.first() ?? ''

    const meta: IActivityMetadata = {
        activity_id: "",
        ad: {
            custom: {
                contact_name: "",
                contact_phone_number: "",
                contact_wechat_qr_string: "",
                entrance_prompt: "我也要免费创建拓客活动",
                popup_prompt: "专业营销获客找管店宝"
            }, status: 'system'
        },
        agent_id: "",
        begin_at: new Date().toISOString(),
        branches: {},
        businesses: {preheating: [], active: [], finished: []},
        create_at: new Date().toISOString(),
        end_at: dayjs().add(7, 'day').toDate().toISOString(),
        preview: {title: '', image_url: ''},
        publish_at: null,
        tags: tags,
        theme: {
            preheating: {
                share_title_template: tt + '[客服：<成员名>]',
                card: Object.values(material.cards.data).filter(c => c.internal_name.includes('公共'))[0],
                "images": [],
                "keys": [0, 0, 0, 0],
                "theme_id": theme_id ?? null
            },
            active: {
                share_title_template: tt + '[客服：<成员名>]',
                card: Object.values(material.cards.data).filter(c => c.internal_name.includes('公共'))[0],
                "images": [],
                "keys": [0, 0, 0, 0],
                "theme_id": theme_id ?? null
            },
            finished: {
                share_title_template: tt + '[客服：<成员名>]',
                card: Object.values(material.cards.data).filter(c => c.internal_name.includes('公共'))[0],
                "images": [],
                "keys": [0, 0, 0, 0],
                "theme_id": theme_id ?? null
            },
        },
        title_template: tt,
        trash_at: null,
        ux: {
            pages: {
                preheating: {action_bar: [], body: [], status_bar: [], popups: []},
                active: {action_bar: [], body: [], status_bar: [], popups: []},
                finished: {action_bar: [], body: [], status_bar: [], popups: []},
            },
            notify_rules: [],
        },
    }
    meta.theme = await api<void, Record<EActivityStatus, IActivityTheme>>({
        url: `user/${ua.meta.account.agent_id}/activity/theme/meta`,
        params: {theme_id, tags}
    })
    meta.theme.preheating.share_title_template = tt + '[客服：<成员名>]'
    meta.theme.active.share_title_template = tt + '[客服：<成员名>]'
    return meta
}